/**
 * Gestão de Perfis e Permissões do painel /wl (Fase 1)
 *
 * - Lista perfis (wl_roles)
 * - Permite criar novo perfil
 * - Permite editar a matriz [menu × ação] para o perfil selecionado
 *
 * RLS garante que somente operadores ativos podem escrever.
 */
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Loader2, Save, Lock } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';
import { createLogger } from '@/lib/logger';
import { describeWlError } from '@/hooks/wl/useWlErrorToast';

const logger = createLogger('WlRoles');

interface WlRoleRow { id: string; name: string; slug: string; description: string | null; is_system: boolean }
interface WlMenuItem { key: string; label: string; group_name: string; sort_order: number }
interface PermRow { menu_key: string; can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean }

const ACTIONS = ['can_view', 'can_create', 'can_edit', 'can_delete'] as const;
const ACTION_LABELS: Record<typeof ACTIONS[number], string> = {
  can_view: 'Ver',
  can_create: 'Criar',
  can_edit: 'Editar',
  can_delete: 'Excluir',
};

export default function WlRoles() {
  const { canCreate, canEdit, isObserver } = useWlPermissions();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<WlRoleRow[]>([]);
  const [menus, setMenus] = useState<WlMenuItem[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [matrix, setMatrix] = useState<Record<string, PermRow>>({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Formulário de novo perfil
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');

  const selectedRole = useMemo(() => roles.find(r => r.id === selectedRoleId) ?? null, [roles, selectedRoleId]);
  // Perfil admin_wl é imutável (sistema): UI bloqueia edição para evitar travamento administrativo
  const isAdminWlRole = selectedRole?.slug === 'admin_wl';

  const writable = canEdit('wl.wl_roles') && !isObserver && !isAdminWlRole;
  const creatable = canCreate('wl.wl_roles') && !isObserver;


  const fetchRolesAndMenus = async () => {
    setLoading(true);
    try {
      // Server-side abstraction: RPC unificado retorna {roles, menus}
      const { data, error } = await supabase.rpc('wl_list_roles_with_menus');
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload = (data as any) ?? { roles: [], menus: [] };
      // Owner WL é ocultado da UI: tem acesso nativo irrestrito, sem matriz a configurar.
      // Admin WL permanece visível porém imutável (bloqueio no front + back).
      const rolesData: WlRoleRow[] = (payload.roles ?? []).filter(
        (r: WlRoleRow) => r.slug !== 'owner_wl'
      );
      const menusData: WlMenuItem[] = payload.menus ?? [];
      setRoles(rolesData);
      setMenus(menusData);
      if (!selectedRoleId && rolesData.length) setSelectedRoleId(rolesData[0].id);
    } catch (err) {
      logger.error('Erro ao carregar perfis', { err });
      toast.error('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatrix = async (roleId: string) => {
    // Server-side abstraction: RPC ao invés de SELECT direto
    const { data, error } = await supabase.rpc('wl_get_role_permissions', { p_role_id: roleId });
    if (error) { toast.error('Erro ao carregar permissões'); return; }
    const map: Record<string, PermRow> = {};
    (data ?? []).forEach((p: PermRow) => { map[p.menu_key] = p; });
    // Garante linha para cada menu, mesmo sem registro ainda
    menus.forEach(m => {
      if (!map[m.key]) {
        map[m.key] = { menu_key: m.key, can_view: false, can_create: false, can_edit: false, can_delete: false };
      }
    });
    setMatrix(map);
  };

  useEffect(() => { fetchRolesAndMenus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (selectedRoleId && menus.length) fetchMatrix(selectedRoleId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedRoleId, menus.length]);

  const togglePerm = (menuKey: string, action: typeof ACTIONS[number]) => {
    if (!writable) return;
    setMatrix(prev => ({
      ...prev,
      [menuKey]: { ...prev[menuKey], [action]: !prev[menuKey][action] },
    }));
  };

  const handleSaveMatrix = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      // Server-side abstraction: UPSERT atômico via RPC
      const permissions = Object.values(matrix).map(p => ({
        menu_key: p.menu_key,
        can_view: p.can_view,
        can_create: p.can_create,
        can_edit: p.can_edit,
        can_delete: p.can_delete,
      }));
      const { error } = await supabase.rpc('wl_save_role_permissions', {
        p_role_id: selectedRoleId,
        p_permissions: permissions,
      });
      if (error) throw error;
      toast.success('Permissões salvas');
    } catch (err) {
      logger.error('Erro ao salvar permissões', { err });
      toast.error(describeWlError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!name.trim() || !slug.trim()) { toast.error('Nome e slug são obrigatórios'); return; }
    setSubmitting(true);
    try {
      // Server-side abstraction: RPC ao invés de INSERT direto
      const { data, error } = await supabase.rpc('wl_create_role', {
        p_name: name.trim(),
        p_slug: slug.trim(),
        p_description: description.trim() || null,
      });
      if (error) throw error;
      toast.success('Perfil criado');
      setOpen(false);
      setName(''); setSlug(''); setDescription('');
      await fetchRolesAndMenus();
      if (data) setSelectedRoleId(data as string);
    } catch (err) {
      logger.error('Erro ao criar perfil', { err });
      toast.error(describeWlError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Agrupa menus por grupo
  const grouped = useMemo(() => {
    const g: Record<string, WlMenuItem[]> = {};
    menus.forEach(m => { (g[m.group_name] ??= []).push(m); });
    return g;
  }, [menus]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Perfis e Permissões</h1>
          <p className="text-sm text-muted-foreground">Configure acesso granular por menu e ação</p>
        </div>
        {creatable && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Novo Perfil</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Novo Perfil</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Nome</Label><Input value={name} onChange={e => setName(e.target.value)} maxLength={80} /></div>
                <div className="space-y-2"><Label>Slug (identificador único)</Label><Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="suporte_n1" maxLength={50} /></div>
                <div className="space-y-2"><Label>Descrição (opcional)</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={300} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancelar</Button>
                <Button onClick={handleCreateRole} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Coluna esquerda: lista de perfis */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Perfis</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {roles.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedRoleId(r.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedRoleId === r.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{r.name}</span>
                  {r.is_system && <Lock className="h-3 w-3 text-muted-foreground" />}
                </div>
                {r.description && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{r.description}</div>}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Coluna direita: matriz */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {selectedRole?.name ?? 'Selecione um perfil'}
                {selectedRole?.is_system && <Badge variant="outline" className="text-[10px]">Sistema</Badge>}
              </CardTitle>
              {selectedRole?.description && <p className="text-xs text-muted-foreground mt-1">{selectedRole.description}</p>}
            </div>
            {writable && selectedRoleId && (
              <Button onClick={handleSaveMatrix} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Salvar Permissões
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!selectedRoleId ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Selecione um perfil para editar permissões.</p>
            ) : (
              <div className="space-y-6">
                {isAdminWlRole && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                    <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Perfil de sistema imutável. O Admin WL sempre possui acesso total a todas as telas do painel para
                      evitar travamento administrativo.
                    </span>
                  </div>
                )}
                {Object.entries(grouped).map(([group, items]) => (
                  <div key={group}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group}</div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/2">Menu</TableHead>
                          {ACTIONS.map(a => <TableHead key={a} className="text-center">{ACTION_LABELS[a]}</TableHead>)}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map(menu => {
                          const row = matrix[menu.key] ?? { menu_key: menu.key, can_view: false, can_create: false, can_edit: false, can_delete: false };
                          return (
                            <TableRow key={menu.key}>
                              <TableCell className="font-medium">{menu.label}</TableCell>
                              {ACTIONS.map(a => (
                                <TableCell key={a} className="text-center">
                                  <Checkbox
                                    // Admin WL: força marcado (acesso total) e bloqueia interação
                                    checked={isAdminWlRole ? true : row[a]}
                                    disabled={!writable || isAdminWlRole}
                                    onCheckedChange={() => togglePerm(menu.key, a)}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            )}
          </CardContent>

        </Card>
      </div>
    </div>
  );
}
